/**
 * Sentry initialization.
 *
 * Called once from `main.tsx`, before the app renders. A missing DSN is not
 * an error — it means `docs/TODO.md`'s "Sentry-DSN eintragen" step hasn't
 * happened yet, and the game must run identically either way.
 */

import * as Sentry from '@sentry/react';
import { getSentryConfig } from './sentry-config';

let initialized = false;

export function initSentry(): void {
  const config = getSentryConfig();
  if (!config) return;

  Sentry.init({
    dsn: config.dsn,
    // The GDD's release cadence isn't automated yet; a static tag is enough
    // to separate crash reports until CI stamps a real version.
    release: 'spacegame@0.1.0',
    // Session Replay and full tracing are premium-quota features the free
    // tier burns through fast; keep this to error capture until traffic
    // volume justifies turning them on (docs/TODO.md tracks that decision).
    tracesSampleRate: 0,
  });
  initialized = true;
}

/** True once `initSentry` has actually configured the SDK (DSN present). */
export function sentryEnabled(): boolean {
  return initialized;
}

/**
 * Report an exception the game caught but didn't expect — anything that
 * reaches here is a bug, not a rejected player action (`ActionError`s never
 * get this far; see `GameProvider`'s `act` callback).
 */
export function captureGameError(error: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return;
  Sentry.captureException(error, context ? { extra: context } : undefined);
}
